import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Screen } from '@/components/layout/Screen';
import { useQuery } from '@tanstack/react-query';
import { getAuthenticatedClient, getCurrentUserId } from '@/services/client';
import { format } from 'date-fns';
import { ChevronRight } from 'lucide-react';
import agronomistAvatar from '@/assets/agronomist-avatar.png';

interface VoiceConversationSummary {
  id: string;
  user_transcription: string | null;
  response_text: string | null;
  language: string;
  created_at: string | null;
}

export const PastQuestionsScreen: React.FC = () => {
  const navigate = useNavigate();
  const currentUserId = getCurrentUserId();

  const { data: conversations, isLoading } = useQuery({
    queryKey: ['voice_conversations_all', currentUserId],
    queryFn: async () => {
      if (!currentUserId) return [];
      const client = getAuthenticatedClient();
      const { data, error } = await client
        .from('voice_conversations')
        .select('id, user_transcription, response_text, language, created_at')
        .eq('user_id', currentUserId)
        .eq('status', 'completed')
        .order('created_at', { ascending: false });
      if (error) {
        console.error('Failed to fetch conversations:', error);
        return [];
      }
      return (data || []) as VoiceConversationSummary[];
    },
    enabled: !!currentUserId,
    staleTime: 1000 * 60 * 5,
  });

  return (
    <Screen
      showTopBar={true}
      topBarProps={{
        title: 'Past Questions',
        showMapPin: false,
        showSettings: false,
      }}
      showBottomNav={false}
      className="flex flex-col bg-background"
    >
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {isLoading ? (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-24 rounded-2xl bg-muted animate-pulse" />
            ))}
          </div>
        ) : conversations && conversations.length > 0 ? (
          <div className="space-y-3">
            {conversations.map((conv) => (
              <button
                key={conv.id}
                onClick={() => navigate(`/agent/history/${conv.id}`)}
                className="w-full border border-border rounded-2xl bg-card p-4 text-left active:scale-[0.98] transition-all"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">
                    {conv.created_at ? format(new Date(conv.created_at), 'dd/MM/yyyy') : '—'}
                  </span>
                  <span className="text-xs font-medium text-success bg-success/10 px-2 py-0.5 rounded-full">
                    Ready!
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <p className="text-sm text-foreground line-clamp-2">
                      {conv.user_transcription
                        ? conv.user_transcription.slice(0, 80) + (conv.user_transcription.length > 80 ? '...' : '')
                        : 'Voice question'}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center px-6">
            <div className="w-20 h-20 rounded-full overflow-hidden mb-4">
              <img src={agronomistAvatar} alt="AgriAgent" className="w-full h-full object-cover" />
            </div>
            <p className="text-sm text-muted-foreground">
              No questions yet. Hold the mic to ask AgriAgent!
            </p>
          </div>
        )}
      </div>
    </Screen>
  );
};
